import { useMemo } from "react";
import { useContactList } from "../../../store/api/useContactList";
import LoadingSkeletonImageDynamic from "../../../components/LoadingSkeletonImageDynamic";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks";
import {
  addOrRemoveUserId,
  updateCreateGroupData,
} from "../../../store/Slices/CreateGroupSlice";
import { ClipLoader } from "react-spinners";
import { useTheme } from "../../../context/ThemeProvider";
import useApiPost from "../../../hooks/PostData";
import toast from "react-hot-toast";
import { useGroupSettings } from "../../../store/api/useGroupSettings";
import { useConversationMembers } from "../../../store/api/useConversationMembers";
import { useTranslateText } from "../../../hooks/useTranslateText";
import SearchNotFoundAnimation from "../../../components/SearchNotFoundAnimation";
import TextTranslate from "../../../utils/TextTranslate";
import { IoCheckmark } from "react-icons/io5";

const groupClass =
  "overflow-hidden rounded-2xl bg-primary shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06]";
const divide =
  "[&>*+*]:border-t [&>*+*]:border-black/[0.06] dark:[&>*+*]:border-white/[0.08]";

type SelectablePerson = {
  user_id: number;
  name: string;
  user_name: string;
  profile_pic: string;
};

export default function AddMemberContactList({
  searchUser,
}: {
  searchUser: string;
}) {
  // @ts-ignore
  const { theme } = useTheme();
  const { isLoading: isContactLoading, data: contactListUser } =
    useContactList();
  const CreateGroup = useAppSelector((state) => state.CreateGroup);
  const CurrentConversation = useAppSelector(
    (state) => state.CurrentConversation,
  );
  const userData = useAppSelector((state) => state.userData);
  const ChatListArray = useAppSelector((state) => state.chatList);
  const archiveList = useAppSelector((state) => state.archiveList);
  const OnlineUserList = useAppSelector((state) => state.OnlineUserList);
  const dispatch = useAppDispatch();
  const { loading, postData } = useApiPost();
  const { refetch } = useConversationMembers();
  const { data: groupSettingsData } = useGroupSettings();
  const translate = useTranslateText();

  async function addMemberToGroup() {
    await postData("chat/add-member", {
      user_id: CreateGroup.user_id,
      chat_id: CurrentConversation.chat_id,
    });

    dispatch(
      updateCreateGroupData({
        show_add_member_modal: false,
        existing_member_user_id: [],
        user_id: [],
      }),
    );
    toast.success(
      `${CreateGroup.user_id.length} ${translate("members added to group")}`,
    );

    refetch();
  }

  const people = useMemo(() => {
    const byId = new Map<number, SelectablePerson>();

    (contactListUser?.data ?? []).forEach((contact) => {
      if (!contact.user_id || contact.user_id === userData.user_id) return;
      byId.set(contact.user_id, {
        user_id: contact.user_id,
        name: contact.name || contact.user_name,
        user_name: contact.user_name,
        profile_pic: contact.profile_pic,
      });
    });

    const chats = [
      ...(ChatListArray.Chats ?? []),
      ...(archiveList.Chats ?? []),
    ];
    chats.forEach((chat) => {
      if (chat.Records?.[0]?.chat_type !== "private") return;
      const peer = chat.PeerUserData;
      if (!peer?.user_id || peer.user_id === userData.user_id) return;
      if (byId.has(peer.user_id)) return;
      const savedName = contactListUser?.data?.find(
        (c) => c.user_id === peer.user_id,
      )?.name;
      byId.set(peer.user_id, {
        user_id: peer.user_id,
        name:
          savedName?.trim() ||
          peer.full_name?.trim() ||
          peer.user_name?.trim() ||
          `#${peer.user_id}`,
        user_name: peer.user_name || "",
        profile_pic: peer.profile_pic || "",
      });
    });

    const q = searchUser.trim().toLowerCase();
    return Array.from(byId.values())
      .filter((person) => {
        if (!q) return true;
        return (
          person.name.toLowerCase().includes(q) ||
          person.user_name.toLowerCase().includes(q)
        );
      })
      .sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
      );
  }, [
    contactListUser?.data,
    ChatListArray.Chats,
    archiveList.Chats,
    userData.user_id,
    searchUser,
  ]);

  if (isContactLoading) {
    return (
      <div className="flex h-60 items-center justify-center">
        <ClipLoader color={theme == "dark" ? "white" : "black"} size={22} />
      </div>
    );
  }

  if (!people.length && !searchUser.trim()) {
    return (
      <div className="grid place-content-center gap-4 py-16 text-center">
        <SearchNotFoundAnimation />
        <div className="text-[15px] text-lightText">
          {translate("No contacts found")}
        </div>
      </div>
    );
  }

  if (!people.length) {
    return (
      <div className="py-16 text-center text-[15px] text-lightText">
        {translate("No contacts found")}
      </div>
    );
  }

  return (
    <>
      <div className={`${groupClass} ${divide}`}>
        {people.map((e) => {
          const alreadyIn = CreateGroup.existing_member_user_id.includes(
            e?.user_id,
          );
          const selected = CreateGroup.user_id.includes(e?.user_id);
          const isOnline = OnlineUserList?.onlineUsers?.some(
            (user) => user.user_id == e?.user_id,
          );

          return (
            <button
              type="button"
              key={e.user_id}
              disabled={alreadyIn}
              onClick={() => {
                if (alreadyIn) return;
                if (
                  CreateGroup.user_id.length >=
                    Number(groupSettingsData?.settings[0].max_members) &&
                  !selected
                ) {
                  toast.error(
                    `Max group memeber size is ${groupSettingsData?.settings[0].max_members ?? 10}`,
                    { position: "bottom-left" },
                  );
                  return;
                }
                dispatch(addOrRemoveUserId({ user_id: e.user_id }));
              }}
              className={`flex w-full items-center gap-3 px-4 py-3 text-left transition active:bg-black/[0.03] dark:active:bg-white/[0.04] ${
                alreadyIn ? "cursor-default opacity-55" : "cursor-pointer"
              } ${selected ? "bg-black/[0.03] dark:bg-white/[0.04]" : ""}`}
            >
              <div
                className={`grid h-6 w-6 shrink-0 place-content-center rounded-full border ${
                  selected || alreadyIn
                    ? "border-themeColor bg-themeColor text-themeTextColor"
                    : "border-black/20 dark:border-white/25"
                }`}
              >
                {(selected || alreadyIn) && (
                  <IoCheckmark className="text-sm" />
                )}
              </div>

              <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-full">
                <LoadingSkeletonImageDynamic
                  radius="9999px"
                  className="h-11 w-11 rounded-full object-cover"
                  image_height="2.75rem"
                  image_url={e?.profile_pic}
                  image_width="2.75rem"
                />
                {isOnline && (
                  <img
                    className="absolute bottom-0 right-0 z-30 h-3.5 w-3.5"
                    src="/Home/Online_Green_dot.png"
                    alt=""
                  />
                )}
              </div>

              <div className="min-w-0 flex-1">
                <div className="truncate text-[15px] font-medium capitalize text-darkText">
                  {e?.name}
                </div>
                <div className="truncate text-[13px] text-lightText">
                  {e?.user_name}
                </div>
              </div>

              {alreadyIn && (
                <span className="shrink-0 text-[12px] text-lightText">
                  <TextTranslate text="In group" />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {CreateGroup.user_id.length > 0 && (
        <div className="absolute inset-x-0 bottom-0 border-t border-black/[0.06] bg-secondary/95 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur-xl dark:border-white/[0.08]">
          <button
            type="button"
            onClick={() => void addMemberToGroup()}
            disabled={loading}
            className="w-full rounded-xl bg-themeColor py-3.5 text-center text-[17px] font-semibold text-themeTextColor transition active:scale-[0.99] disabled:opacity-60"
          >
            {loading ? (
              <ClipLoader
                size={19}
                color={theme == "dark" ? "white" : "black"}
              />
            ) : (
              <>
                <TextTranslate text="Add Member" />
                {CreateGroup.user_id.length > 1
                  ? ` (${CreateGroup.user_id.length})`
                  : ""}
              </>
            )}
          </button>
        </div>
      )}
    </>
  );
}
